import { useState, useEffect, useCallback } from 'react';
import {
  Loader2,
  ArrowLeftRight,
  TrendingUp,
  TrendingDown,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Filter,
} from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeader } from '../../components/ui';
import { StatCard, Badge, card } from '../../design-system';
import { formatCurrency } from '../../utils/format';

interface FluxoDia {
  data: string;
  entradas: number;
  saidas: number;
  saldo: number;
  saldoAcumulado: number;
}

interface ResumoFluxo {
  saldoInicial: number;
  totalEntradas: number;
  totalSaidas: number;
  saldoFinal: number;
}

export function FluxoCaixa() {
  const { showApiError } = useNotification();

  const [fluxo, setFluxo] = useState<FluxoDia[]>([]);
  const [resumo, setResumo] = useState<ResumoFluxo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mesAno, setMesAno] = useState(() => {
    const hoje = new Date();
    return { mes: hoje.getMonth() + 1, ano: hoje.getFullYear() };
  });
  const [contaId, setContaId] = useState<string>('');
  const [contas, setContas] = useState<Array<{ id: number; nome: string }>>([]);

  const fetchContas = useCallback(async () => {
    try {
      const response = await api.get('/financeiro/contas');
      setContas(response.data);
    } catch (error) {
      showApiError(error);
    }
  }, [showApiError]);

  const fetchFluxo = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        mes: mesAno.mes.toString(),
        ano: mesAno.ano.toString(),
      });
      if (contaId) params.append('contaId', contaId);

      const response = await api.get(`/financeiro/fluxo-caixa?${params}`);
      setFluxo(response.data.dias || []);
      setResumo(response.data.resumo || null);
    } catch (error) {
      showApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, [mesAno, contaId, showApiError]);

  useEffect(() => {
    fetchContas();
  }, [fetchContas]);

  useEffect(() => {
    const controller = new AbortController();
    fetchFluxo();
    return () => controller.abort();
  }, [fetchFluxo]);

  const handleMesAnterior = () => {
    setMesAno((prev) => {
      if (prev.mes === 1) {
        return { mes: 12, ano: prev.ano - 1 };
      }
      return { mes: prev.mes - 1, ano: prev.ano };
    });
  };

  const handleProximoMes = () => {
    setMesAno((prev) => {
      if (prev.mes === 12) {
        return { mes: 1, ano: prev.ano + 1 };
      }
      return { mes: prev.mes + 1, ano: prev.ano };
    });
  };

  const meses = [
    'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
  ];

  const formatarDia = (data: string) => {
    const [, , dia] = data.split('-');
    const diaSemana = new Date(data + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'short' });
    return { dia: parseInt(dia), diaSemana };
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Fluxo de Caixa" subtitle="Acompanhe a movimentacao financeira">
        <div className="flex items-center gap-4">
          <select
            value={contaId}
            onChange={(e) => setContaId(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Todas as contas</option>
            {contas.map((conta) => (
              <option key={conta.id} value={conta.id}>{conta.nome}</option>
            ))}
          </select>
        </div>
      </PageHeader>

      {/* Navegacao de Mes */}
      <div className={`${card} p-4`}>
        <div className="flex items-center justify-between">
          <button
            onClick={handleMesAnterior}
            className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-400" />
            <span className="text-lg font-bold tracking-tight text-slate-900">
              {meses[mesAno.mes - 1]} de {mesAno.ano}
            </span>
          </div>
          <button
            onClick={handleProximoMes}
            className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Cards de Resumo */}
      {resumo && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            title="Saldo Inicial"
            value={formatCurrency(resumo.saldoInicial)}
            icon={<ArrowLeftRight className="h-[18px] w-[18px]" />}
            tone={resumo.saldoInicial >= 0 ? 'default' : 'negative'}
            delay={0}
          />
          <StatCard
            title="Total Entradas"
            value={formatCurrency(resumo.totalEntradas)}
            icon={<TrendingUp className="h-[18px] w-[18px]" />}
            tone="positive"
            delay={60}
          />
          <StatCard
            title="Total Saidas"
            value={formatCurrency(resumo.totalSaidas)}
            icon={<TrendingDown className="h-[18px] w-[18px]" />}
            tone="negative"
            delay={120}
          />
          <StatCard
            title="Saldo Final"
            value={formatCurrency(resumo.saldoFinal)}
            icon={<ArrowLeftRight className="h-[18px] w-[18px]" />}
            tone={resumo.saldoFinal >= 0 ? 'positive' : 'negative'}
            delay={180}
          />
        </div>
      )}

      {/* Tabela de Fluxo */}
      <div className={`${card} overflow-hidden`}>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : fluxo.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Filter className="w-12 h-12 mx-auto mb-2 text-slate-300" />
            <p>Nenhuma movimentacao no periodo</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Dia</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Entradas</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Saidas</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Saldo Dia</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Saldo Acumulado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {fluxo.map((dia) => {
                  const { dia: numDia, diaSemana } = formatarDia(dia.data);
                  const isHoje = dia.data === new Date().toISOString().split('T')[0];

                  return (
                    <tr
                      key={dia.data}
                      className={`hover:bg-slate-50 ${isHoje ? 'bg-primary-50' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`font-semibold ${isHoje ? 'text-primary-600' : 'text-slate-900'}`}>
                            {numDia}
                          </span>
                          <span className="text-sm text-slate-400 capitalize">{diaSemana}</span>
                          {isHoje && <Badge tone="primary">Hoje</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {dia.entradas > 0 ? (
                          <span className="text-emerald-600 font-semibold tabular-nums">
                            +{formatCurrency(dia.entradas)}
                          </span>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {dia.saidas > 0 ? (
                          <span className="text-rose-600 font-semibold tabular-nums">
                            -{formatCurrency(dia.saidas)}
                          </span>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-semibold tabular-nums ${dia.saldo >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {dia.saldo >= 0 ? '+' : ''}{formatCurrency(dia.saldo)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-bold tabular-nums ${dia.saldoAcumulado >= 0 ? 'text-slate-900' : 'text-rose-600'}`}>
                          {formatCurrency(dia.saldoAcumulado)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
